import {
  Bus,
  Download,
  FileText,
  MapPin,
  Route as RouteIcon,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react';

import { DonutChart, type DonutSlice } from '@/components/ui/DonutChart';
import { cn } from '@/lib/utils';
import type { FleetStanding, TransportAlert, TransportDocument } from '@/types/transport';
import { ALERT_STYLE } from '../constants';

/**
 * Everything down the right of the dashboard: how the fleet stands, what has
 * just happened, the four things an office starts from, and the papers it is
 * asked for.
 *
 * One component rather than four files, because the four blocks are one column
 * — they share a width, a rhythm and a card treatment, and splitting them would
 * mean four imports to move a rail that always moves together. Each block is a
 * `<section>` with its own heading, so the split is still there for a reader.
 */

export interface QuickAction {
  key: string;
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
}

export interface TransportSideRailProps {
  standing: FleetStanding;
  alerts: TransportAlert[];
  documents: TransportDocument[];
  actions: QuickAction[];
  onViewReport?: () => void;
  onViewAllAlerts?: () => void;
  onViewAllDocuments?: () => void;
  onDownloadDocument?: (document: TransportDocument) => void;
}

export function TransportSideRail({
  standing,
  alerts,
  documents,
  actions,
  onViewReport,
  onViewAllAlerts,
  onViewAllDocuments,
  onDownloadDocument,
}: TransportSideRailProps) {
  const slices: DonutSlice[] = [
    {
      key: 'onRoute',
      label: 'On route',
      value: standing.onRoute,
      stroke: 'stroke-emerald-500',
      dot: 'bg-emerald-500',
    },
    {
      key: 'delayed',
      label: 'Delayed',
      value: standing.delayed,
      stroke: 'stroke-amber-500',
      dot: 'bg-amber-500',
    },
    {
      key: 'notStarted',
      label: 'Not started',
      value: standing.notStarted,
      stroke: 'stroke-rose-500',
      dot: 'bg-rose-500',
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <RailCard title="Today's overview" action={onViewReport && { label: 'View report', onSelect: onViewReport }}>
        <DonutChart
          slices={slices}
          total={standing.totalVehicles}
          caption="Total vehicles"
          layout="responsive"
        />
      </RailCard>

      <RailCard
        title="Recent notifications"
        action={onViewAllAlerts && { label: 'View all', onSelect: onViewAllAlerts }}
      >
        <ul className="flex flex-col gap-1">
          {alerts.map((alert, index) => {
            const style = ALERT_STYLE[alert.kind];
            const Icon = style.icon;
            return (
              <li
                key={alert.uuid}
                className="flex items-start gap-3 rounded-2xl px-2 py-2 transition-colors hover:bg-surface-2 animate-fade-up"
                style={{ animationDelay: `${Math.min(index, 5) * 50}ms` }}
              >
                <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-xl', style.tile)} aria-hidden>
                  <Icon className="h-4 w-4" />
                </span>
                <p className="min-w-0 flex-1 text-sm font-semibold leading-snug text-ink-2">
                  {alert.message}
                </p>
                <time className="shrink-0 text-xs font-bold text-ink-3">{alert.at}</time>
              </li>
            );
          })}
        </ul>
      </RailCard>

      <RailCard title="Quick actions">
        <QuickActionGrid actions={actions} />
      </RailCard>

      <RailCard
        title="Documents"
        action={onViewAllDocuments && { label: 'View all', onSelect: onViewAllDocuments }}
      >
        <ul className="flex flex-col gap-1">
          {documents.map((document) => (
            <li
              key={document.uuid}
              className="flex items-center gap-3 rounded-2xl px-2 py-2 transition-colors hover:bg-surface-2"
            >
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-crit-bg text-crit" aria-hidden>
                <FileText className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-bold text-ink">{document.name}</span>
                <span className="block text-xs font-semibold text-ink-3">
                  {document.format} · {document.sizeLabel}
                </span>
              </span>
              <button
                type="button"
                onClick={() => onDownloadDocument?.(document)}
                title={`Download ${document.name}`}
                aria-label={`Download ${document.name}`}
                className="grid h-8 w-8 shrink-0 place-items-center rounded-xl text-ink-3 transition-colors hover:bg-surface-3 hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                <Download className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      </RailCard>
    </div>
  );
}

/** The rail's card: a heading, an optional trailing link, and a body. */
export function RailCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: { label: string; onSelect: () => void } | null;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-3xl border border-line bg-surface shadow-sm">
      <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <h2 className="text-base font-bold text-ink">{title}</h2>
        {action && (
          <button
            type="button"
            onClick={action.onSelect}
            className="rounded-pill px-2.5 py-1 text-sm font-bold text-accent transition-colors hover:bg-accent-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            {action.label}
          </button>
        )}
      </header>
      <div className="p-3.5">{children}</div>
    </section>
  );
}

/**
 * The rail's three-across grid of shortcuts.
 *
 * Shared by both rails: the dashboard's six and the Routes tab's six are
 * different actions drawn the same way, which is a prop rather than a second
 * copy of this markup (CLAUDE.md §3).
 */
export function QuickActionGrid({ actions }: { actions: QuickAction[] }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {actions.map((action) => {
        const Icon = action.icon;
        return (
          <button
            key={action.key}
            type="button"
            onClick={action.onSelect}
            className="flex flex-col items-center gap-1.5 rounded-2xl border border-line bg-surface-2 px-2 py-3 text-center transition-all hover:-translate-y-0.5 hover:border-accent-line hover:bg-accent-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <span
              className="grid h-9 w-9 place-items-center rounded-xl bg-surface text-accent shadow-sm"
              aria-hidden
            >
              <Icon className="h-4 w-4" />
            </span>
            <span className="text-[11px] font-bold leading-tight text-ink-2">{action.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** The four things the office starts a transport day from. */
export const QUICK_ACTION_ICONS = {
  addRoute: RouteIcon,
  addVehicle: Bus,
  addDriver: Users,
  assignStudents: UserPlus,
  reports: FileText,
  tracking: MapPin,
} satisfies Record<string, LucideIcon>;
